// Alert engine contract (ADR 0001 §2). Rules turn feed values into owned,
// trackable work: open → acknowledged → resolved, every transition recorded.
import type { FallbackTier } from "./types";

export type AlertSeverity = "info" | "watch" | "warning" | "critical";
export type AlertStatus = "open" | "acknowledged" | "resolved";
export type AlertOp = ">" | ">=" | "<" | "<=" | "==" | "!=";
export type AlertScalar = number | string | boolean | null;

export const ALERT_SEVERITY_RANK: Record<AlertSeverity, number> = {
  info: 0,
  watch: 1,
  warning: 2,
  critical: 3,
};

/** One comparison against a record field (dotted path, e.g. "meta.aqi"). */
export interface AlertCondition {
  metric: string;
  op: AlertOp;
  value: AlertScalar;
}

/** Escalating band on the rule's metric; the highest matching band wins. */
export interface AlertLevel {
  severity: AlertSeverity;
  op: AlertOp;
  value: AlertScalar;
}

export type AlertRuleSource =
  /** Cache key of a built-in feed; "air-quality" also matches "air-quality:v3". */
  | { feed: string }
  /** Connector manifest id (ADR 0001 §1). */
  | { connector: string }
  /** A table in our own SQLite store, evaluated on the engine timer. */
  | { dataset: "campus-reports" }
  /** Static modeled GeoJSON under apps/web/public — always `scenario` tier. */
  | { file: string };

export interface AlertWhen {
  /** Per-record: field to compare (also the {{value}} of the message). */
  metric?: string;
  op?: AlertOp;
  value?: AlertScalar;
  /** Per-record escalating bands on `metric` (instead of op/value). */
  levels?: AlertLevel[];
  /** Group-level: number of records passing `where` compared to a threshold. */
  count?: { op: AlertOp; value: number };
  /** The condition must hold across evaluations for this long before opening. */
  forMinutes?: number;
  /**
   * Ignore records whose observation time (fields.observedAt, default
   * "observedAt") is older than this or unparseable — a two-month-old gauge
   * reading is not a current condition. Zone-less timestamps are read as
   * Asia/Bangkok (+07:00), which is what the Thai upstreams send.
   */
  maxAgeMinutes?: number;
}

export interface AlertRule {
  id: string;
  title: string;
  severity: AlertSeverity;
  source: AlertRuleSource;
  /** Record filter applied before `when` (all must pass). */
  where?: AlertCondition[];
  when: AlertWhen;
  /** Template: {{value}} {{label}} {{metric}}. */
  message: string;
  /** One line: who does what first. */
  runbook: string;
  notify?: Array<"telegram">;
  enabled: boolean;
  /** Fixed record id when the feed represents one place (e.g. campus AQI). */
  recordId?: string;
  /** Record field paths overriding the default id/label lookups. */
  fields?: { id?: string; label?: string; observedAt?: string };
  /** Records are dashboard incidents: the alert carries a deep link to the issue card. */
  link?: "incident";
}

/** Deep link from an alert to the thing it is about (e.g. a citizen report card). */
export interface AlertLink {
  kind: "incident";
  id: string;
  ticket?: string;
  /** Public title (the same text the public incident feed already shows). */
  title?: string;
}

export interface Alert {
  id: string;
  ruleId: string;
  /** ruleId + recordId — one alert per condition per record, ever. */
  dedupKey: string;
  title: string;
  message: string;
  severity: AlertSeverity;
  status: AlertStatus;
  /** Human-readable source (feed key / connector id / dataset / file). */
  sourceRef: string;
  /** Provenance tier inherited from the source at the last evaluation. */
  sourceTier: FallbackTier;
  /** True when the source is modeled (`scenario`) — never notified as real. */
  simulated: boolean;
  recordId?: string;
  label?: string;
  /** [lng, lat] */
  location?: [number, number];
  value?: AlertScalar;
  link?: AlertLink;
  /**
   * The condition has cleared (two consecutive known-clear evaluations) but
   * the alert is critical, so it stays open until a named human resolves it.
   */
  conditionCleared: boolean;
  openedAt: string;
  lastSeenAt: string;
  acknowledgedAt: string | null;
  acknowledgedBy: string | null;
  assignee: string | null;
  resolvedAt: string | null;
  resolvedBy: string | null;
  reopenCount: number;
  runbook: string;
}

export interface AlertEvent {
  alertId: string;
  ts: string;
  actor: string;
  /** null for the opening event. */
  from: AlertStatus | null;
  to: AlertStatus;
  note: string | null;
}

export type AlertChange =
  | "opened"
  | "reopened"
  | "escalated"
  | "deescalated"
  | "acknowledged"
  | "assigned"
  | "resolved"
  | "auto-resolved"
  | "condition-cleared"
  | "note";

/** Engine status carried next to GET /api/alerts so an empty queue is honest. */
export interface AlertEngineInfo {
  running: boolean;
  lastEvaluatedAt: string | null;
  rules: number;
  ruleErrors: number;
}
